/**
 * Bulletin de paie PDF — Structure LPG (fiduciaire Luxembourg)
 * Calque exact sur le modele de reference fourni.
 */

import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from '@react-pdf/renderer'
import type { Style } from '@react-pdf/types'
import type { BulletinData } from './bulletin-types'

/** Format centimes → "1 234,56" */
function fmt(cents: number | null): string {
  if (cents == null) return ''
  const n = cents / 100
  const [intPart, decPart] = Math.abs(n).toFixed(2).split('.')
  const withSpaces = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  const sign = n < 0 ? '-' : ''
  return `${sign}${withSpaces},${decPart}`
}

function fmtQte(n: number | null): string {
  if (n == null) return ''
  return n % 1 === 0 ? n.toFixed(2) : n.toFixed(2)
}

function fmtBase(cents: number | null): string {
  if (cents == null) return ''
  return (cents / 100).toFixed(4).replace('.', ',')
}

// ─── Styles ──────────────────────────────────────────────────

const C = { black: '#000000', grey: '#666666', lightGrey: '#e5e5e5', white: '#ffffff', accent: '#1e40af' }

const s = StyleSheet.create({
  page: { fontFamily: 'Helvetica', fontSize: 7.5, color: C.black, padding: 30, paddingBottom: 40 },

  // Header
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
  headerLeft: { width: '55%' },
  headerRight: { width: '42%', border: '1px solid #000', padding: 6 },
  companyName: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  companyAddr: { fontSize: 7.5, color: C.grey },
  title: { fontSize: 12, fontFamily: 'Helvetica-Bold', marginBottom: 6 },
  empName: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  empAddr: { fontSize: 7.5 },

  // Info grid
  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', borderTop: '1px solid #000', borderLeft: '1px solid #000', marginBottom: 8 },
  infoCell: { width: '16.66%', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: 2 },
  infoCellWide: { width: '33.33%', borderRight: '1px solid #000', borderBottom: '1px solid #000', padding: 2 },
  infoLabel: { fontSize: 6, color: C.grey },
  infoValue: { fontSize: 7.5, fontFamily: 'Helvetica-Bold' },

  // Body table
  table: { borderTop: '1px solid #000', borderLeft: '1px solid #000', marginBottom: 8 },
  tableHeader: { flexDirection: 'row', borderBottom: '1px solid #000', backgroundColor: '#f0f0f0' },
  tableRow: { flexDirection: 'row', borderBottom: '0.5px solid #ccc', minHeight: 12 },
  tableRowBold: { flexDirection: 'row', borderBottom: '1px solid #000', backgroundColor: '#f0f0f0', minHeight: 14 },
  colCode: { width: '8%', borderRight: '0.5px solid #ccc', padding: 2, fontSize: 7 } as Style,
  colLib: { width: '32%', borderRight: '0.5px solid #ccc', padding: 2 } as Style,
  colQte: { width: '10%', borderRight: '0.5px solid #ccc', padding: 2, textAlign: 'right' } as Style,
  colBase: { width: '15%', borderRight: '0.5px solid #ccc', padding: 2, textAlign: 'right' } as Style,
  colTotal: { width: '17.5%', borderRight: '0.5px solid #ccc', padding: 2, textAlign: 'right' } as Style,
  colRetenue: { width: '17.5%', borderRight: '1px solid #000', padding: 2, textAlign: 'right' } as Style,
  headerText: { fontSize: 7, fontFamily: 'Helvetica-Bold' },
  bold: { fontFamily: 'Helvetica-Bold' },

  // Summary bar
  summaryBar: { flexDirection: 'row', border: '1.5px solid #000', marginBottom: 8 },
  summaryCell: { flex: 1, padding: 3, textAlign: 'center', borderRight: '0.5px solid #000' },
  summaryLabel: { fontSize: 6, color: C.grey, marginBottom: 1 },
  summaryValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },

  // Cumuls + Conges (bottom)
  bottomRow: { flexDirection: 'row', gap: 8 },
  bottomBox: { flex: 1, border: '1px solid #000' },
  bottomTitle: { fontSize: 7, fontFamily: 'Helvetica-Bold', padding: 2, borderBottom: '1px solid #000', backgroundColor: '#f0f0f0' },
  bottomRow2: { flexDirection: 'row', borderBottom: '0.5px solid #ccc', padding: 1 },
  bottomLabel: { width: '45%', fontSize: 6.5 },
  bottomVal: { width: '27.5%', fontSize: 7, textAlign: 'right' },

  // Patronal
  patronalSection: { marginTop: 6, border: '1px solid #000', padding: 4 },
  patronalTitle: { fontSize: 7, fontFamily: 'Helvetica-Bold', marginBottom: 3 },
  patronalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 1 },
  patronalLabel: { fontSize: 6.5 },
  patronalValue: { fontSize: 7, textAlign: 'right' },

  // Footer
  footer: { position: 'absolute', bottom: 15, left: 30, right: 30, flexDirection: 'row', justifyContent: 'space-between', fontSize: 6, color: C.grey },
})

// ─── Component ───────────────────────────────────────────────

export function BulletinPDF({ data }: { data: BulletinData }) {
  const d = data
  const p = d.period
  const e = d.employee
  const sm = d.summary

  return (
    <Document title={`Bulletin de Paie — ${e.lastName} ${e.firstName} — ${p.startDate}`} author="MIREY">
      <Page size="A4" style={s.page}>

        {/* ─── HEADER ─────────────────────────────── */}
        <View style={s.headerRow}>
          <View style={s.headerLeft}>
            <Text style={s.title}>Bulletin de Paie</Text>
            <Text style={s.companyName}>{d.company.name}</Text>
            <Text style={s.companyAddr}>{d.company.address}</Text>
          </View>
          <View style={s.headerRight}>
            <Text style={s.empName}>{e.civilite}</Text>
            <Text style={s.empName}>{e.lastName} {e.firstName}</Text>
            <Text style={s.empAddr}>{e.address}</Text>
            <View style={{ flexDirection: 'row', marginTop: 4, gap: 8 }}>
              <View><Text style={s.infoLabel}>Profession</Text><Text style={s.infoValue}>{e.profession}</Text></View>
              <View><Text style={s.infoLabel}>Mat. Societe</Text><Text style={s.infoValue}>{d.company.matSociete}</Text></View>
            </View>
          </View>
        </View>

        {/* ─── INFO GRID ──────────────────────────── */}
        <View style={s.infoGrid}>
          <View style={s.infoCell}><Text style={s.infoLabel}>Code Societe</Text><Text style={s.infoValue}>{d.company.codeSociete}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>Code Sal</Text><Text style={s.infoValue}>{e.codeSal}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>Periode du</Text><Text style={s.infoValue}>{p.startDate}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>au</Text><Text style={s.infoValue}>{p.endDate}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>j.imp</Text><Text style={s.infoValue}>{p.joursImp}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>j.cot</Text><Text style={s.infoValue}>{p.joursCot}</Text></View>

          <View style={s.infoCell}><Text style={s.infoLabel}>No Secu</Text><Text style={s.infoValue}>{e.noSecu}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>D.entree</Text><Text style={s.infoValue}>{e.entryDate}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>D.anciennete</Text><Text style={s.infoValue}>{e.seniorityDate}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>D.sortie</Text><Text style={s.infoValue}>{e.exitDate}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>Hrs/mois</Text><Text style={s.infoValue}>{p.hresMois}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>Cl.imp</Text><Text style={s.infoValue}>{e.taxClass}</Text></View>

          <View style={s.infoCell}><Text style={s.infoLabel}>Indice</Text><Text style={s.infoValue}>{e.indice}</Text></View>
          <View style={s.infoCellWide}><Text style={s.infoLabel}>No carte</Text><Text style={s.infoValue}>{e.noCarte}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>t.imp</Text><Text style={s.infoValue}>{e.taxType}</Text></View>
          <View style={s.infoCell}><Text style={s.infoLabel}>Banque</Text><Text style={s.infoValue}>{e.banque}</Text></View>
        </View>

        {/* ─── BODY TABLE ─────────────────────────── */}
        <View style={s.table}>
          <View style={s.tableHeader}>
            <Text style={[s.colCode, s.headerText]}>C.pr</Text>
            <Text style={[s.colLib, s.headerText]}>Libelle Prestation</Text>
            <Text style={[s.colQte, s.headerText]}>Qte</Text>
            <Text style={[s.colBase, s.headerText]}>Sal.base</Text>
            <Text style={[s.colTotal, s.headerText]}>Total</Text>
            <Text style={[s.colRetenue, s.headerText]}>Retenue</Text>
          </View>
          {d.lines.map((line, i) => (
            <View key={i} style={line.isBold ? s.tableRowBold : s.tableRow}>
              <Text style={[s.colCode, line.isBold ? s.bold : {}]}>{line.code}</Text>
              <Text style={[s.colLib, line.isBold ? s.bold : {}]}>{line.libelle}</Text>
              <Text style={s.colQte}>{fmtQte(line.qte)}</Text>
              <Text style={s.colBase}>{fmtBase(line.salBase)}</Text>
              <Text style={[s.colTotal, line.isBold ? s.bold : {}]}>{fmt(line.total)}</Text>
              <Text style={[s.colRetenue, line.isBold ? s.bold : {}]}>{fmt(line.retenue)}</Text>
            </View>
          ))}
        </View>

        {/* ─── SUMMARY BAR ────────────────────────── */}
        <View style={s.summaryBar}>
          {[
            ['Brut', sm.brutCents],
            ['Cotisations', sm.cotisationsCents],
            ['Abattement', sm.abattementCents],
            ['Imposable', sm.imposableCents],
            ['Impots', sm.impotsCents],
            ['Net', sm.netCents],
            ['A verser', sm.aVerserCents],
          ].map(([label, value], i) => (
            <View key={i} style={s.summaryCell}>
              <Text style={s.summaryLabel}>{label as string}</Text>
              <Text style={s.summaryValue}>{fmt(value as number)}</Text>
            </View>
          ))}
        </View>

        {/* ─── CUMULS + CONGES ────────────────────── */}
        <View style={s.bottomRow}>
          {/* Cumuls */}
          <View style={s.bottomBox}>
            <Text style={s.bottomTitle}>Cumuls</Text>
            {[
              ['Brut', d.cumuls.brutCents, d.cumuls.brutCents],
              ['Cotisations', d.cumuls.cotisationsCents, d.cumuls.cotisationsCents],
              ['Abattement', d.cumuls.abattementCents, d.cumuls.abattementCents],
              ['Imposable', d.cumuls.imposableCents, d.cumuls.imposableCents],
              ['Impots', d.cumuls.impotsCents, d.cumuls.impotsCents],
              ['Net', d.cumuls.netCents, d.cumuls.netCents],
              ['A verser', d.cumuls.aVerserCents, null],
            ].map(([label, val, total], i) => (
              <View key={i} style={s.bottomRow2}>
                <Text style={s.bottomLabel}>{label as string}</Text>
                <Text style={s.bottomVal}>{fmt(val as number | null)}</Text>
                <Text style={s.bottomVal}>{total != null ? fmt(total as number) : ''}</Text>
              </View>
            ))}
          </View>

          {/* Conges */}
          <View style={s.bottomBox}>
            <Text style={s.bottomTitle}>Conges</Text>
            {[
              ['Rep.conges', d.conges.repConges],
              ['C.sup', d.conges.congeSup],
              ['C.legaux', d.conges.congeLegaux],
              ['C.pris', d.conges.congePris],
              ['Sold.conge', d.conges.soldeCongeDays],
            ].map(([label, val], i) => (
              <View key={i} style={s.bottomRow2}>
                <Text style={s.bottomLabel}>{label as string}</Text>
                <Text style={s.bottomVal}>{typeof val === 'number' ? val.toFixed(2) : ''}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ─── CHARGES PATRONALES ─────────────────── */}
        <View style={s.patronalSection}>
          <Text style={s.patronalTitle}>CHARGES PATRONALES</Text>
          {[
            ['Maladie (Soins) 2,80%', d.patronal.maladieSoinsCents],
            ['Maladie (Especes) 0,25%', d.patronal.maladieEspecesCents],
            ['Pension 8,50%', d.patronal.pensionCents],
            ['Accident du travail', d.patronal.accidentCents],
            ['Mutualite', d.patronal.mutualiteCents],
            ['Sante au travail', d.patronal.santeCents],
          ].map(([label, val], i) => (
            <View key={i} style={s.patronalRow}>
              <Text style={s.patronalLabel}>{label as string}</Text>
              <Text style={s.patronalValue}>{fmt(val as number)}</Text>
            </View>
          ))}
          <View style={[s.patronalRow, { borderTop: '1px solid #000', paddingTop: 2, marginTop: 2 }]}>
            <Text style={[s.patronalLabel, s.bold]}>TOTAL PATRONAL</Text>
            <Text style={[s.patronalValue, s.bold]}>{fmt(d.patronal.totalCents)}</Text>
          </View>
          <View style={s.patronalRow}>
            <Text style={[s.patronalLabel, s.bold]}>COUT TOTAL EMPLOYEUR</Text>
            <Text style={[s.patronalValue, s.bold]}>{fmt(d.totalCostCents)}</Text>
          </View>
        </View>

        {/* ─── FOOTER ─────────────────────────────── */}
        <View style={s.footer} fixed>
          <Text>Genere par MIREY</Text>
          <Text>CONFIDENTIEL</Text>
        </View>
      </Page>
    </Document>
  )
}
